import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import Papa from 'papaparse'
import { FaSearch, FaTrash, FaChevronDown, FaPlus, FaEdit, FaCheck, FaSortAlphaDown, FaSortAlphaUp, FaCopy } from 'react-icons/fa'
import { Alert, Button, Input, InputGroup, Icon, Heading, Text, Box, Stack, ResponsiveStack, SimpleTable, Menu, MenuItem, MenuGroup, Tag, ColouredTag, Wrap, WrapItem, Checkbox, BottomStickyBar, EmptyCard, Badge } from '@/components/lib'
import utils from '@/utils/utils'
import useStore from '../../store'
import api from '../../api'

import EmptyBox from '../includes/EmptyBox'
import ConfirmButton from '../includes/ConfirmButton'
import CSVChooser from '../includes/CSVChooser'
import DataTypeTagCreator from '../includes/DataTypeTagCreator'
import useTitle from '../../hooks/useTitle'
import DataTypeEditor from './NewData'
import DataImage from '../../images/dataTypes.png'
import { categories } from '../trials/NewData'

function Data () {
  const [filter, setFilter] = useState('')
  const [selectedDataTypes, setSelectedDataTypes] = useState([])
  const [selectedDataTypeTag, setSelectedDataTypeTag] = useState()
  const [formatsInUse, setFormatsInUse] = useState([])
  const [selectedFormat, setSelectedFormat] = useState()
  const [newlyCreatedDataType, setNewlyCreatedDataType] = useState(null)
  const [sortKey, setSortKey] = useState('name')
  const [sortDirection, setSortDirection] = useState(1)
  const [page, setPage] = useState(1)
  const { id } = useParams()
  const PAGE_SIZE = 100
  const { user, trial, updateTrial, dataTypeTags, dataTypes, addDataType, deleteDataType } = useStore()
  // Data-type CRUD and tag operations are both gated on trial.write on the API.
  const canWrite = utils.canWriteTrial(user, trial)
  useTitle('Data Types', trial)

  // Fetch new data types in case changed in background
  useEffect(() => {
    if (!id) return
    api.trials.getDataTypes(id, ({ dataTypes }) => {
      addDataType(dataTypes)
    }, err => utils.toast('error', 'Unable to fetch data types', err.message))
  }, [id, addDataType])

  useEffect(() => {
    if (dataTypes.length) {
      const formats = []
      dataTypes.forEach(d => {
        if (formats.indexOf(d.type) === -1) formats.push(d.type)
      })
      setFormatsInUse(formats)
    }
  }, [dataTypes])

  // Reset to page 1 when search (name) or tag filter changes
  useEffect(() => {
    setPage(1)
  }, [filter, selectedDataTypeTag])

  function onCreated (newDataType) {
    addDataType(newDataType)
    setNewlyCreatedDataType(newDataType)
    const newDataTypeElement = document.querySelector(`[data-data-type-id='${newDataType._id}']`)
    setTimeout(() => {
      if (newDataTypeElement) {
        newDataTypeElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 200)
  }

  function onDeleteDataType (id) {
    api.dataTypes.delete(id, () => {
      utils.toast('success', 'Data type deleted')
      deleteDataType(id)
      updateTrial(trial._id, { dataTypeCount: (trial.dataTypeCount ?? 1) - 1 })
    }, err => utils.toast('error', 'Unable to delete data type', err.message))
  }

  function clone (d) {
    api.trials.createDataType(trial._id, { copyFrom: d._id }, dataType => {
      utils.toast('success', 'Data type created')
      addDataType(dataType)
      setNewlyCreatedDataType(dataType)
      updateTrial(trial._id, { dataTypeCount: (trial.dataTypeCount ?? 0) + 1 })
    }, err => utils.toast('error', 'Unable to clone data type', err.message))
  }

  function csvFormatter (text) {
    const types = []
    categories.forEach(c => c.types.forEach(t => types.push(t.key)))
    const dat = Papa.parse(text, { skipEmptyLines: true, header: true, transformHeader: h => h.toLowerCase(), transform: v => v.trim() })
    return [['name', 'type', 'nickname'], dat.data.map(({ type, name, nickname }) => {
      const row = { type, name, nickname }
      if (types.indexOf(type) === -1) row.error = 'Unknown data type'
      return row
    })]
  }
  function bulkCreateDataTypes (types) {
    const validTypes = types.filter(t => !t.error)
    if (!validTypes.length) {
      utils.toast('error', 'Unable to import file', 'There are no valid data types to create')
      return
    }
    api.trials.createDataType(trial._id, { dataTypes: validTypes }, ({ dataTypes }) => {
      dataTypes.forEach(d => addDataType(d))
      updateTrial(trial._id, { dataTypeCount: (trial.dataTypeCount ?? 0) + dataTypes.length })
      utils.toast('success', 'Imported successfully')
    }, err => {
      utils.toast('error', 'Unable to import file', err.message)
    })
  }

  function addToTag (dataTypeId, tagId) {
    const dataType = dataTypes.find(d => d._id === dataTypeId)
    if (!dataType.tags) dataType.tags = []
    const index = dataType.tags.indexOf(tagId)
    if (index === -1) {
      dataType.tags.push(tagId)
    } else {
      dataType.tags.splice(index, 1)
    }
    api.dataTypes.update(dataTypeId, { tags: dataType.tags }, () => {
      addDataType(dataType)
    })
  }

  function bulkAddToTag (tagId) {
    api.dataTypes.updateBulk(selectedDataTypes, { addTags: [tagId] }, (d) => {
      d.dataTypes.forEach(dt => addDataType(dt))
    })
  }

  function bulkDelete () {
    api.dataTypes.deleteBulk(selectedDataTypes, () => {
      selectedDataTypes.forEach(id => deleteDataType(id))
      setSelectedDataTypes([])
      utils.toast('success', 'Selected data types deleted')
    }, err => utils.toast('error', 'Unable to delete data types', err.message))
  }

  let filteredData = filter ? dataTypes.filter(d => d.name.toLowerCase().includes(filter) || d.nickname?.toLowerCase()?.includes(filter)) : dataTypes
  if (selectedDataTypeTag) filteredData = filteredData.filter(d => d.tags?.includes(selectedDataTypeTag._id))
  if (selectedFormat) filteredData = filteredData.filter(d => d.type === selectedFormat)
  filteredData = filteredData.sort((a, b) => {
    if (sortKey === 'name') {
      const name = (a.nickname || a.name).toLowerCase()
      const name2 = (b.nickname || b.name).toLowerCase()
      return name === name2 ? 0 : name > name2 ? sortDirection : -1 * sortDirection
    } else if (sortKey === 'type') {
      return a.type === b.type ? 0 : a.type > b.type ? sortDirection : -1 * sortDirection
    }
    return 0
  })

  const paginatedData = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <Stack gap={5}>
      <Stack direction='row' justifyContent='space-between'>
        <Box>
          <Heading as='h2' size='lg'>Data types</Heading>
          <Text>The variables (types of data) collected as part of this trial.</Text>
        </Box>
        {canWrite &&
          <ResponsiveStack>
            <CSVChooser
              trigger={<Button colorPalette='primary' variant='outline'>Bulk create data types</Button>}
              title='Bulk create data types for this trial'
              instructions='Upload a CSV file to create lots of data types in one go. Your CSV must include a "name" and "type" column.'
              sample={{ filename: 'datatype_sample.csv', headings: ['name', 'type', 'nickname'], rows: [['sleep_quality', 'slider', 'Sleep Quality'], ['last_visit_date', 'date', 'Last Visit'], ['wake_up_time', 'time', 'Wake-up time'], ['average_sleep_hours', 'integer', 'Hours of Sleep']] }}
              formatFunction={csvFormatter}
              onComplete={bulkCreateDataTypes}
            />
            <DataTypeEditor
              trial={trial}
              onComplete={onCreated}
              trigger={
                <Button colorPalette='primary'>Create a new data type (variable)</Button>
              }
            />
          </ResponsiveStack>}
      </Stack>

      {dataTypes?.length > 0 &&
        <Stack gap={5}>
          <Stack direction='row'>
            <InputGroup leftIcon={<FaSearch />} size='sm'>
              <Input w='300px' size='sm' placeholder='Type to search your data types...' value={filter} onChange={e => setFilter(e.target.value.toLowerCase())} />
            </InputGroup>
            <Menu
              closeOnSelect={false}
              trigger={
                <Button size='sm' variant='outline'>
                  {selectedDataTypeTag ? selectedDataTypeTag.name : 'Filter by tag'}
                  <Icon><FaChevronDown /></Icon>
                </Button>
}
            >
              <MenuItem value='all' onClick={e => setSelectedDataTypeTag()}>
                {!selectedDataTypeTag && <Icon as={FaCheck} />}
                All data types
              </MenuItem>
              {dataTypeTags?.length > 0 && <hr />}
              {dataTypeTags.map(tag => (
                <MenuItem key={tag._id} value={tag._id} onClick={e => setSelectedDataTypeTag(tag)} display='flex' justifyContent='space-between'>
                  <Stack direction='row' alignItems='center'>
                    {selectedDataTypeTag?._id === tag._id && <Icon as={FaCheck} />}
                    <ColouredTag colour={tag.colour}>{tag.name}</ColouredTag>
                  </Stack>
                  {canWrite &&
                    <DataTypeTagCreator
                      trigger={<Button size='3xs' variant='ghost'><Icon><FaEdit /></Icon></Button>}
                      dataTypeTag={tag}
                    />}
                </MenuItem>
              ))}
              {canWrite &&
                <>
                  <hr />
                  <DataTypeTagCreator
                    trigger={<MenuItem value='new'><Icon><FaPlus /></Icon> Create a tag</MenuItem>}
                  />
                </>}
            </Menu>
            <Menu
              trigger={
                <Button size='sm' variant='outline'>{selectedFormat ?? 'Filter by format'} <Icon><FaChevronDown /></Icon></Button>
              }
            >
              <MenuItem value='all' onClick={e => setSelectedFormat()}>
                {!selectedFormat && <Icon as={FaCheck} />}
                All formats
              </MenuItem>
              {formatsInUse?.length > 0 && <hr />}
              {formatsInUse.map(format => (
                <MenuItem key={format} value={format} onClick={e => setSelectedFormat(format)}>
                  {selectedFormat === format && <Icon as={FaCheck} />}
                  {format}
                </MenuItem>
              ))}
            </Menu>
          </Stack>

          {filteredData?.length > 0
            ? (
              <>
                <SimpleTable
                  asCard
                  size='sm'
                  headers={[
                    <Menu
                      key='bulk'
                      closeOnSelect={false}
                      trigger={<Button size='xs' variant='ghost'>{selectedDataTypes?.length || 0} <Icon><FaChevronDown /></Icon></Button>}
                    >
                      <MenuItem value='none' onClick={e => setSelectedDataTypes([])}>De-select all</MenuItem>
                      <MenuItem value='all' onClick={e => setSelectedDataTypes(filteredData.map(d => d._id))}>Select all data types</MenuItem>
                      <MenuItem value='page' onClick={e => setSelectedDataTypes(prev => [...new Set([...prev, ...paginatedData.map(d => d._id)])])}>Select all on current page</MenuItem>
                      {selectedDataTypes?.length > 0 &&
                        <>
                          {canWrite &&
                            <>
                              <hr />
                              <ConfirmButton
                                asSubMenu
                                trigger={<MenuItem value='delete'>Delete selected data types</MenuItem>}
                                header='Really delete the selected data types?'
                                footer='You cannot delete data types if they are being used by a stage.'
                                onConfirm={e => bulkDelete()}
                                actionColor='red'
                              />
                            </>}
                          {canWrite && dataTypeTags?.length > 0 && <hr />}
                          {canWrite && dataTypeTags?.length > 0 &&
                            <MenuGroup title='Add a tag'>
                              {dataTypeTags.map(t =>
                                <MenuItem key={t._id} value={t._id} onClick={e => bulkAddToTag(t._id)}>
                                  <ColouredTag size='sm' colour={t.colour}>{t.name}</ColouredTag>
                                </MenuItem>)}
                            </MenuGroup>}
                        </>}
                    </Menu>,
                    <Stack
                      direction='row' alignItems='center' gap={1} key='name' style={{ cursor: 'pointer' }} onClick={e => {
                        setSortDirection(sortDirection * -1)
                        setSortKey('name')
                      }} role='button'
                    >
                      Name
                      {sortKey === 'name' &&
                        <Icon as={sortDirection > 0 ? FaSortAlphaDown : FaSortAlphaUp} />}
                    </Stack>,
                    <Stack
                      direction='row' alignItems='center' gap={1} key='type' style={{ cursor: 'pointer' }} onClick={e => {
                        setSortDirection(sortDirection * -1)
                        setSortKey('type')
                      }} role='button'
                    >
                      Format
                      {sortKey === 'type' &&
                        <Icon as={sortDirection > 0 ? FaSortAlphaDown : FaSortAlphaUp} />}
                    </Stack>,
                    'Tags',
                    ''
                  ]}
                  rows={paginatedData.map(d => [
                    <Stack direction='row' justifyContent='start' key={d._id} data-data-type-id={d._id}>
                      {canWrite &&
                        <Checkbox
                          isChecked={selectedDataTypes.includes(d._id)} onChange={e => {
                            const index = selectedDataTypes.indexOf(d._id)
                            if (index === -1) {
                              setSelectedDataTypes([...selectedDataTypes, d._id])
                            } else {
                              setSelectedDataTypes(selectedDataTypes.filter(id => id !== d._id))
                            }
                          }}
                        />}
                      {newlyCreatedDataType?._id === d._id &&
                        <Tag size='sm' colorPalette='green'>New</Tag>}
                    </Stack>,
                    <Stack key={d._id} gap={0} maxW='260px'>
                      <Text fontWeight='bold' wordBreak='break-all'>
                        {d.nickname || d.name}
                      </Text>
                      {d.nickname && (
                        <Text fontSize='xs' color='fg.muted' wordBreak='break-all'>
                          {d.name}
                        </Text>
                      )}
                    </Stack>,
                    <Stack key={d._id} alignItems='start'>
                      <Stack direction='row' alignItems='center' gap={1}>
                        <Badge key={d._id}>{d.type}</Badge>
                        {d.isPii && <Badge colorPalette='red' size='xs'>PII</Badge>}
                        {d.isRestricted && <Badge colorPalette='yellow' size='xs'>Restricted</Badge>}
                      </Stack>
                      {d.type === 'promisForm' && <Badge colorPalette='blue' size='xs'>{d.promisFormName}</Badge>}
                      {d.type === 'promisBattery' && <Badge colorPalette='blue' size='xs'>{d.promisBatteryName} ({d.promisForms?.length || 0} forms)</Badge>}
                      {d.type === 'promisBattery' && d.promisProprEligible != null &&
                        <Badge colorPalette={d.promisProprEligible ? 'green' : 'gray'} size='xs'>{d.promisProprEligible ? 'PROPr scorable' : 'No PROPr score'}</Badge>}
                    </Stack>,
                    <Wrap key={d._id} alignItems='center'>
                      {d.tags?.map(tagId => {
                        const tag = dataTypeTags?.find(t => t._id === tagId)
                        return tag &&
                          <WrapItem key={tag._id}>
                            <ColouredTag colour={tag.colour} onClose={canWrite ? () => addToTag(d._id, tagId) : undefined}>{tag.name}</ColouredTag>
                          </WrapItem>
                      })}
                      {canWrite && dataTypeTags?.length > 0 &&
                        <WrapItem>
                          <Menu
                            trigger={<Button size='2xs' variant='ghost'><Icon as={FaPlus} /></Button>}
                          >
                            {dataTypeTags?.map(t =>
                              <MenuItem key={t._id} value={t._id} onClick={e => addToTag(d._id, t._id)}>{t.name}</MenuItem>
                            )}
                          </Menu>
                        </WrapItem>}
                    </Wrap>,
                    <Stack direction='row' justifyContent='flex-end' key={d._id}>
                      {canWrite &&
                        <>
                          <DataTypeEditor
                            trial={trial} dataTypeId={d._id}
                            onComplete={newDataType => {
                              addDataType(newDataType)
                            }}
                            trigger={
                              <Button size='sm' colorPalette='primary'>Edit</Button>
                          }
                          />
                          <Button size='sm' variant='ghost' onClick={e => clone(d)}><Icon as={FaCopy} /></Button>
                          <ConfirmButton
                            trigger={<Button size='sm' variant='ghost'><FaTrash /></Button>}
                            header='Really delete this data type?'
                            footer='You cannot delete a data type if it is being used by a stage.'
                            onConfirm={e => onDeleteDataType(d._id)}
                            actionColor='red'
                          />
                        </>}
                    </Stack>
                  ])}
                />
                {filteredData.length >= PAGE_SIZE && (
                  <BottomStickyBar justifyContent='center' gap={3}>
                    {(() => {
                      const totalPages = Math.ceil(filteredData.length / PAGE_SIZE) || 1
                      const start = (page - 1) * PAGE_SIZE + 1
                      const end = Math.min(page * PAGE_SIZE, filteredData.length)
                      return (
                        <>
                          <Button
                            size='sm'
                            variant='outline'
                            disabled={page <= 1}
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                          >
                            Previous
                          </Button>
                          <Text fontSize='sm' whiteSpace='nowrap'>
                            {start}–{end} of {filteredData.length}
                          </Text>
                          <Button
                            size='sm'
                            variant='outline'
                            disabled={page >= totalPages}
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          >
                            Next
                          </Button>
                          {totalPages > 1 && (
                            <Text fontSize='sm' color='fg.muted'>
                              Page {page} of {totalPages}
                            </Text>
                          )}
                        </>
                      )
                    })()}
                  </BottomStickyBar>
                )}
              </>)
            : <Alert>Your search returned no results. Please try changing your search term.</Alert>}
        </Stack>}
      {!dataTypes?.length &&
        <>
          <Stack direction='row' alignItems='center' justifyContent='center'>
            <EmptyCard icon='data' title='Data types' description='Set up and manage the variables that you want to collect in your trial. You can select from simple input types right the way through to image and video uploads, cognitive tests, or validated questionnaires.' />
          </Stack>
          <EmptyBox
            imagee={DataImage} title='Get started' description={canWrite ? 'Create your first data type using the button below.' : 'No data types have been created yet.'}
          >
            {canWrite &&
              <DataTypeEditor
                trial={trial}
                onComplete={onCreated}
                trigger={
                  <Button colorPalette='primary'>Define a new data type</Button>
                  }
              />}
          </EmptyBox>
        </>}
    </Stack>
  )
}

export default Data
